/**
 * Checks a call's input against the tool's own input schema before the tool runs, so a malformed call
 * is refused naming the bad part instead of failing inside the tool. Covers the parts of JSON Schema
 * the tools' schemas use: type, enum, required, properties, additionalProperties and items.
 */

type Schema = Record<string, unknown>;

/** Main adds these two to every tool's schema; they are the call's, not the tool's. */
const CALL_FIELDS = ['request', 'intent'];

export function withoutCallFields(input: unknown): unknown {
  if (!isObject(input)) return input;
  const rest = { ...input };
  for (const key of CALL_FIELDS) delete rest[key];
  return rest;
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function typeOf(v: unknown): string {
  if (v === null) return 'null';
  if (Array.isArray(v)) return 'array';
  if (typeof v === 'number') return Number.isInteger(v) ? 'integer' : 'number';
  return typeof v;
}

const A_TYPE: Record<string, string> = {
  object: 'an object', array: 'a list', string: 'a string', number: 'a number', integer: 'a whole number',
  boolean: 'true or false', null: 'null',
};

function fitsType(v: unknown, type: string): boolean {
  const actual = typeOf(v);
  return actual === type || (type === 'number' && actual === 'integer');
}

/** The first way `value` breaks `schema`, worded for the agent; null when it fits. */
export function inputProblem(schema: Schema | undefined, value: unknown, path = 'the input'): string | null {
  if (!schema) return null;

  const types = schema['type'] === undefined ? [] : ([] as unknown[]).concat(schema['type']).map(String);
  if (types.length && !types.some((t) => fitsType(value, t))) {
    return `${path} must be ${types.map((t) => A_TYPE[t] ?? t).join(' or ')}.`;
  }

  const allowed = schema['enum'];
  if (Array.isArray(allowed) && !allowed.includes(value)) {
    return `${path} must be one of ${allowed.map((a) => JSON.stringify(a)).join(', ')}.`;
  }

  if (isObject(value)) {
    const props = (schema['properties'] ?? {}) as Record<string, Schema>;
    for (const key of (schema['required'] ?? []) as string[]) {
      if (value[key] === undefined) return `${path} is missing "${key}".`;
    }
    for (const [key, v] of Object.entries(value)) {
      if (props[key]) {
        const problem = inputProblem(props[key], v, `${path === 'the input' ? '' : `${path}.`}${key}`);
        if (problem) return problem;
      } else if (schema['additionalProperties'] === false) {
        const known = Object.keys(props);
        return `${path} has "${key}", which isn't taken here. ${known.length ? `It takes: ${known.join(', ')}.` : 'It takes nothing.'}`;
      }
    }
  }

  if (Array.isArray(value) && isObject(schema['items'])) {
    for (let i = 0; i < value.length; i++) {
      const problem = inputProblem(schema['items'] as Schema, value[i], `${path === 'the input' ? 'the list' : path}[${i}]`);
      if (problem) return problem;
    }
  }

  return null;
}
